import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button } from '@structural-climbing/ui'
import { CurrentSubscriptionCard, type ProfileSubscriptionData } from './ProfileSubscription'

interface Props {
  addons: ProfileSubscriptionData[]
  /** Look, don't touch: no "Use session". */
  readOnly?: boolean
}

/**
 * The add-ons a member (or child) holds next to their main plan, each with a
 * "Use session" button. A desk check-in never touches an add-on, so this is the
 * only place one gets spent. Seeded once from the profile fetch; the modal
 * refetches by remounting.
 */
export function AddonsSection({ addons: initial, readOnly = false }: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [addons, setAddons] = useState(initial)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (addons.length === 0) return null

  async function spendSession(id: string) {
    setBusyId(id)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/subscriptions/${encodeURIComponent(id)}/use`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        setError(body.error ?? t('members.addonUseError'))
        return
      }
      const body = (await res.json()) as {
        entries_remaining: number | null
        effective_status: ProfileSubscriptionData['status']
      }
      setAddons((prev) =>
        prev.map((a) =>
          a.id === id
            ? { ...a, entriesRemaining: body.entries_remaining, status: body.effective_status }
            : a,
        ),
      )
    } catch {
      setError(t('members.addonUseError'))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-foreground/40">
        {t('members.addons')}
      </p>
      <div className="flex flex-col gap-1.5">
        {addons.map((addon) => (
          <div key={addon.id} className="flex flex-col gap-1.5">
            <CurrentSubscriptionCard sub={addon} />
            {addon.status === 'active' && !readOnly && (
              <Button
                size="sm"
                variant="flat"
                color="primary"
                className="self-end"
                isLoading={busyId === addon.id}
                isDisabled={busyId !== null}
                onClick={() => void spendSession(addon.id)}
              >
                {t('members.addonUse')}
              </Button>
            )}
          </div>
        ))}
      </div>
      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </div>
  )
}
